import { Box, FormControl, InputLabel, MenuItem, Select, TextField, Checkbox, ListItemText, CircularProgress } from "@mui/material";
import { generateClient } from "aws-amplify/api";
import { Formik } from "formik";
import { enqueueSnackbar } from "notistack";
import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { array, object, string } from "yup";
import { PageAppBar } from "../../components/pageAppBar";
import { UserContext } from "../../contexts/UserContext";
import { getManualProfile, listSuppressions, profileByTenant, segmentByTenant } from "../../graphql/queries";
import { createManualProfile, updateManualProfile } from "../../graphql/mutations";
import { InContact } from "../../contexts/InContact";
import { fetchAllPages } from "../../tools/graphqlPaging";


export function ManualProfile() {
    const client = generateClient();
    const { id } = useParams();
    const userContext = useContext(UserContext)
    const navigate = useNavigate();
    const [manualProfile, setManualProfile] = useState({
        id: '',
        name: '',
        dialingProfile: '',
        suppressionProfile: '',
        segment: '',
        skills: '',
        manualStrategy: 'profileSelect',
    });

    const [segments, setSegments] = useState([]);
    const [profiles, setProfiles] = useState([]);
    const [suppressions, setSuppressions] = useState([]);
    const [skills, setSkills] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (userContext.apiKey) {
            getData();
        }
    }, [userContext.apiKey]);

    useEffect(() => {
        if (id && id !== 'new') {
            getProfile();
        }
    }, [id]);

    async function getData() {
        setLoading(true);
        const promises = [];
        promises.push(getSegments());
        promises.push(getProfiles());
        promises.push(getSuppressions());
        promises.push(InContact.getSSValidSkills());

        const queries = await Promise.all(promises);

        setSegments(queries[0]);
        setProfiles(queries[1]);
        setSuppressions(queries[2]);
        setSkills(queries[3]);
        setLoading(false);
    }

    async function getSegments() {
        try {
            return await fetchAllPages({
                client,
                query: segmentByTenant,
                resultKey: 'segmentByTenant',
                variables: { tenant: userContext.tenantId },
            });
        } catch (error) {
            console.error("Error fetching segments:", error);
        }
    }

    async function getProfiles() {
        try {
            return await fetchAllPages({
                client,
                query: profileByTenant,
                resultKey: 'profileByTenant',
                variables: { tenant: userContext.tenantId },
            });
        } catch (error) {
            console.error("Error fetching profiles:", error);
        }
    }

    async function getSuppressions() {
        try {
            return await fetchAllPages({
                client,
                query: listSuppressions,
                resultKey: 'listSuppressions',
                variables: { tenant: userContext.tenantId },
            });
        } catch (error) {
            console.error("Error fetching suppressions:", error);
        }
    }

    async function getProfile() {
        try {
            const dynamoManualProfile = await client.graphql({
                query: getManualProfile,
                variables: { id },
            });

            const profile = dynamoManualProfile.data.getManualProfile;
            // Default existing profiles without manualStrategy to segment search
            if (!profile.manualStrategy) {
                profile.manualStrategy = 'profileSelect';
            }
            setManualProfile(profile);
        } catch (error) {
            console.error("Error fetching manual profile:", error);
        }
    }

    async function handleSave(values) {
        try {
            if (values.id) {
                delete values.createdAt;
                delete values.updatedAt;
            }
            if (values.id === "") {
                delete values.id;
            }
            values.updatedBy = userContext.email;
            const savedProfile = await client.graphql({
                query: id === 'new' ? createManualProfile : updateManualProfile,
                variables: {
                    input: {
                        ...values,
                        tenant: userContext.tenantId
                    }
                },
            });

            if (id === 'new') {
                navigate(`/manual-profiles/${savedProfile.data.createManualProfile.id}`);
            }

            enqueueSnackbar('Profile saved successfully.', {
                variant: 'success',
                autoHideDuration: 2000
            });
        } catch (error) {
            console.error("Error saving manual profile:", error);
            enqueueSnackbar('Error saving profile.', {
                variant: 'error',
                autoHideDuration: 2000
            });
        }
    }

    return (
        <>
            <Formik
                initialValues={manualProfile}
                enableReinitialize={true}
                validationSchema={
                    object({
                        name: string().required("Name is required"),
                        dialingProfile: string().required("Dialing profile is required"),
                        suppressionProfile: string().required("Suppression profile is required"),
                        segment: string().when('manualStrategy', {
                            is: (val) => val !== 'agentLastCall',
                            then: (schema) => schema.required("Segment is required"),
                            otherwise: (schema) => schema.notRequired(),
                        }),
                        skills: string().required("A skill is required"),
                        manualStrategy: string().required("Contact source is required"),
                    })
                }
                onSubmit={(values) => {
                    handleSave(values);
                }}
            >
                {formikProps => (
                    <form onSubmit={formikProps.handleSubmit}>
                        <Box display="flex" flexDirection="column" gap={2}>
                            <PageAppBar title={manualProfile.name || "Manual Profile"}
                                actionOneText={userContext.securityOptions.communicationProfiles > 1 ? "Save" : ""}
                                actionOneHandler={() => {
                                    formikProps.handleSubmit();
                                }}
                                actionTwoText="Cancel"
                                actionTwoHandler={() => {
                                    navigate('/manual-profiles');
                                }}
                            />
                            {!loading && <Box display="flex" flexDirection="column" gap={2}>
                                <TextField
                                    label="Profile Name"
                                    name="name"
                                    value={formikProps.values.name}
                                    onChange={formikProps.handleChange}
                                    fullWidth
                                />
                                <FormControl>
                                    <InputLabel>Communication Profile</InputLabel>
                                    <Select
                                        label="Communication Profile"
                                        name="dialingProfile"
                                        value={formikProps.values.dialingProfile}
                                        onChange={formikProps.handleChange}
                                        fullWidth
                                    >
                                        {profiles.map(profile => (
                                            <MenuItem key={profile.id} value={profile.id}>
                                                {profile.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <FormControl>
                                    <InputLabel>Suppression Profile</InputLabel>
                                    <Select
                                        label="Suppression Profile"
                                        name="suppressionProfile"
                                        value={formikProps.values.suppressionProfile}
                                        onChange={formikProps.handleChange}
                                        fullWidth
                                    >
                                        {suppressions.map(profile => (
                                            <MenuItem key={profile.id} value={profile.id}>
                                                {profile.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <FormControl>
                                    <InputLabel>Contact Source</InputLabel>
                                    <Select
                                        label="Contact Source"
                                        name="manualStrategy"
                                        value={formikProps.values.manualStrategy || 'profileSelect'}
                                        onChange={formikProps.handleChange}
                                        fullWidth
                                    >
                                        <MenuItem value="profileSelect">Segment Search</MenuItem>
                                        <MenuItem value="agentLastCall">Last Contact Dialed</MenuItem>
                                    </Select>
                                </FormControl>
                                {formikProps.values.manualStrategy !== 'agentLastCall' && <FormControl>
                                    <InputLabel>Segment</InputLabel>
                                    <Select
                                        label="Segment"
                                        name="segment"
                                        value={formikProps.values.segment}
                                        onChange={formikProps.handleChange}
                                        fullWidth
                                    >
                                        {segments.map(segment => (
                                            <MenuItem key={segment.id} value={segment.id}>
                                                {segment.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>}
                                <FormControl>
                                    <InputLabel>Skills</InputLabel>
                                    <Select
                                        label="Skill"
                                        name="skills"
                                        value={formikProps.values.skills}
                                        onChange={formikProps.handleChange}
                                        fullWidth
                                    >
                                        {skills.map(skill =>
                                            <MenuItem key={skill.skillId} value={skill.skillId}>
                                                {skill.skillName}
                                            </MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Box>}
                            {loading && <CircularProgress color="primary" />}
                        </Box>
                    </form>
                )}
            </Formik>
        </>
    )

}